import React, { useRef } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import classNames from "classnames";
import { useRootAppStore } from "@hooks";
import { WaitingPeriodPopupTypeEnum } from "@utils/common/waitingPeriodPopupTypeEnum";
import { WaitingPeriodPopupStateEnum } from "@utils/common/waitingPeriodPopupStateEnum";

export default observer(function WaitingPeriodTimer() {
	const { t } = useTranslation("user-account");

	const rootStore = useRootAppStore();

	const { waitingPeriodStore } = rootStore;

	const isFloating =
		waitingPeriodStore.popupState == WaitingPeriodPopupStateEnum.MINIMIZED;

	const TIME_LEFT = Math.ceil(waitingPeriodStore.timeLeft / 1000);

	const durationRef = useRef<number>(
		waitingPeriodStore.getWaitingPeriodState().timerDuration
	);

	const PROGRESS = TIME_LEFT / (durationRef.current / 1000);

	const modifierClass =
		waitingPeriodStore.timeLeft > 0 ? "animated" : "static";

	const wrapperClasses = classNames("countdown", [
		`countdown--${modifierClass}`,
		{
			[`countdown--floating countdown--floating--${modifierClass}`]:
				isFloating,
		},
	]);

	const spinnerClasses = classNames("countdown__spinner", {
		"countdown--floating__spinner": isFloating,
	});

	const trackClasses = classNames(
		"countdown__track",
		[`countdown--${modifierClass}__track`],
		{
			"countdown--floating__track": isFloating,
		}
	);

	const thumbClasses = classNames(
		"countdown__thumb",
		[`countdown--${modifierClass}__thumb`],
		{
			"countdown--floating__thumb": isFloating,
		}
	);

	const contentClasses = classNames("countdown__content", {
		"countdown--floating__content": isFloating,
	});

	const labelClasses = classNames("countdown__label", {
		"countdown--floating__label": isFloating,
	});

	const timerClasses = classNames("countdown__timer", {
		"countdown--floating__timer": isFloating,
	});

	if (isFloating) {
		const RADIUS_SML = 28;

		// Don't change these variables
		const CIRCUMFERENCE_SML = 2 * Math.PI * RADIUS_SML;
		const OFFSET_SML = CIRCUMFERENCE_SML * PROGRESS;

		return (
			<div
				onClick={() =>
					waitingPeriodStore.popupType ==
					WaitingPeriodPopupTypeEnum.SUBMIT
						? waitingPeriodStore.showPopup(
								WaitingPeriodPopupTypeEnum.SUBMIT
							)
						: waitingPeriodStore.showPopup(
								WaitingPeriodPopupTypeEnum.GAME
							)
				}
				className={wrapperClasses}
			>
				<svg
					className={spinnerClasses}
					width={RADIUS_SML * 2}
					height={RADIUS_SML * 2}
				>
					<circle
						className={trackClasses}
						r={RADIUS_SML}
						cx={RADIUS_SML}
						cy={RADIUS_SML}
					/>

					{waitingPeriodStore.timeLeft > 0 && (
						<circle
							className={thumbClasses}
							r={RADIUS_SML}
							cx={RADIUS_SML}
							cy={RADIUS_SML}
							style={{
								strokeDasharray: CIRCUMFERENCE_SML,
								strokeDashoffset: OFFSET_SML,
								transition: "stroke-dashoffset 1s linear",
							}}
						/>
					)}
				</svg>

				<div className={contentClasses}>
					{waitingPeriodStore.timeLeft > 0 ? (
						<>
							<div className={labelClasses}>
								{t("LUGAS.WAITING_LABEL")}
							</div>
							<div className={timerClasses}>
								{waitingPeriodStore.getFormattedTime}
							</div>
						</>
					) : (
						<>
							<i className="countdown--static__icon u-icon u-icon--med u-icon--arrow-right u-color--accent--text" />
						</>
					)}
				</div>
			</div>
		);
	}

	if (waitingPeriodStore.popupState == WaitingPeriodPopupStateEnum.ACTIVE) {
		const RADIUS_LRG = 50;

		// Don't change these variables
		const CIRCUMFERENCE_LRG = 2 * Math.PI * RADIUS_LRG;
		const OFFSET_LRG = CIRCUMFERENCE_LRG * PROGRESS;

		return (
			<div className={wrapperClasses}>
				<svg
					className={spinnerClasses}
					width={RADIUS_LRG * 2}
					height={RADIUS_LRG * 2}
				>
					<circle
						className={trackClasses}
						r={RADIUS_LRG}
						cx={RADIUS_LRG}
						cy={RADIUS_LRG}
					/>

					{waitingPeriodStore.timeLeft > 0 && (
						<circle
							className={thumbClasses}
							r={RADIUS_LRG}
							cx={RADIUS_LRG}
							cy={RADIUS_LRG}
							style={{
								strokeDasharray: CIRCUMFERENCE_LRG,
								strokeDashoffset: OFFSET_LRG,
								transition: "stroke-dashoffset 1s linear",
							}}
						/>
					)}
				</svg>

				<div className={contentClasses}>
					{waitingPeriodStore.timeLeft > 0 ? (
						<>
							<div className={labelClasses}>
								{t("LUGAS.WAITING_LABEL")}
							</div>
							<div className={timerClasses}>
								{waitingPeriodStore.getFormattedTime}
							</div>
						</>
					) : (
						<>
							<i className="countdown--static__icon u-icon u-icon--xxlrg u-icon--check u-color--accent--text" />
						</>
					)}
				</div>
			</div>
		);
	}

	return null;
});
